"use client";

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { MobileBottomSheet } from "@/components/shared/MobileBottomSheet";
import {
  fetchCitiesClient,
  localeToGogoLanguageId,
  type GogoCityRecord,
} from "@/lib/gogoMasterDataClient";
import { ensureNavCityFromApi } from "@/lib/navCityBootstrap";
import {
  NAV_CITY_CHANGED_EVENT,
  readSelectedCityFromCookies,
  resolveSelectedNavCityClient,
  writeSelectedCityCookies,
} from "@/lib/navCityCookie";
import {
  isValidNearestCoordPair,
  writePersistedNearestCoords,
} from "@/lib/nearestLocationPreference";
import { fetchRabetCityCoords } from "@/lib/rabetCityGeocode";
import { GTMEvents, GTMSubEvents, PushDataToGTM } from "@/lib/gtm";

const PAGE_SIZE = 100;

function cityLabel(c: GogoCityRecord): string {
  const name = (c.City ?? "").trim();
  if (name) return name;
  const key = (c.CityKey ?? "").trim();
  if (key) return key;
  return `City #${c.CityId}`;
}

const NAV_CITY_MAX_MOBILE_PX = 767;

export function NavCitySelector({ className = "" }: { className?: string }) {
  const { locale, t, isRtl } = useLanguage();
  const languageId = localeToGogoLanguageId(locale);
  const wrapRef = useRef<HTMLDivElement>(null);
  const prevDebouncedSearchKeyRef = useRef<string | null>(null);

  const [isNarrowViewport, setIsNarrowViewport] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(`(max-width: ${NAV_CITY_MAX_MOBILE_PX}px)`).matches
      : false
  );

  const [open, setOpen] = useState(false);
  /** `null` after sync = no saved city (show default); before sync we avoid showing the default prematurely */
  const [savedCityName, setSavedCityName] = useState<string | null>(null);
  /** After first client sync we may show {@link savedCityName} or Riyadh fallback — never the fallback before this */
  const [cityLabelResolved, setCityLabelResolved] = useState(false);
  const [cities, setCities] = useState<GogoCityRecord[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [listPage, setListPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearchKey, setDebouncedSearchKey] = useState("");
  const syncSavedCityFromStorage = useCallback(() => {
    const r = readSelectedCityFromCookies() ?? resolveSelectedNavCityClient();
    if (r) {
      setSavedCityName(locale === "ar" ? r.name : r.nameEn);
      return;
    }
    setSavedCityName(null);
  }, [locale]);

  useLayoutEffect(() => {
    syncSavedCityFromStorage();
    setCityLabelResolved(true);
  }, [syncSavedCityFromStorage]);

  /** Bootstrap UUID + prefetch city list (not only when the picker opens). */
  useEffect(() => {
    let cancelled = false;
    void ensureNavCityFromApi().then(() => {
      if (!cancelled) syncSavedCityFromStorage();
    });
    void fetchCitiesClient(languageId, { page: 1, limit: PAGE_SIZE }).then(
      ({ records, totalRecords: total }) => {
        if (!cancelled) {
          setCities(records);
          setTotalRecords(total);
        }
      }
    );
    return () => {
      cancelled = true;
    };
  }, [languageId, syncSavedCityFromStorage]);

  useEffect(() => {
    const onCityCookies = () => syncSavedCityFromStorage();
    window.addEventListener(NAV_CITY_CHANGED_EVENT, onCityCookies);
    return () =>
      window.removeEventListener(NAV_CITY_CHANGED_EVENT, onCityCookies);
  }, [syncSavedCityFromStorage]);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${NAV_CITY_MAX_MOBILE_PX}px)`);
    const sync = () => setIsNarrowViewport(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const logCityNavClick = useCallback((suffix: string) => {
    const prefix = isNarrowViewport ? "MOBILE HEADER" : "DESKTOP HEADER";
    PushDataToGTM(GTMEvents.DataLayerUpdate, {
      pageName: prefix,
      pageCategory: isNarrowViewport ? "MOBILE MENU" : "DESKTOP MENU",
      eventCategory: GTMSubEvents.Click,
      eventLabel: `${prefix} > ${suffix}`,
    });
  }, [isNarrowViewport]);

  useEffect(() => {
    if (!open) {
      setSearchInput("");
      setDebouncedSearchKey("");
      return;
    }
    const id = window.setTimeout(() => {
      setDebouncedSearchKey(searchInput.trim());
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput, open]);

  useEffect(() => {
    if (!open) {
      prevDebouncedSearchKeyRef.current = null;
      return;
    }
    const q = debouncedSearchKey;
    const prev = prevDebouncedSearchKeyRef.current;
    if (q === "") {
      if (prev !== null && prev !== "") {
        logCityNavClick("CLEAR CITY SEARCH");
      }
      prevDebouncedSearchKeyRef.current = "";
      return;
    }
    if (q !== prev) {
      logCityNavClick(`CITY SEARCH > ${q?.toUpperCase()}`);
    }
    prevDebouncedSearchKeyRef.current = q;
  }, [open, debouncedSearchKey, logCityNavClick]);

  useEffect(() => {
    if (!open) return;
    if (!debouncedSearchKey && cities.length > 0) {
      setLoadError(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(false);
      setListPage(1);
      try {
        const { records, totalRecords: total } = await fetchCitiesClient(
          languageId,
          {
            page: 1,
            limit: PAGE_SIZE,
            searchKey: debouncedSearchKey || undefined,
          }
        );
        if (!cancelled) {
          setCities(records);
          setTotalRecords(total);
        }
      } catch {
        if (!cancelled) {
          setLoadError(true);
          setCities([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, languageId, debouncedSearchKey, cities.length]);

  useEffect(() => {
    if (!open || isNarrowViewport) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current?.contains(e.target as Node)) return;
      logCityNavClick("CLOSE CITY POPUP");
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, isNarrowViewport, logCityNavClick]);

  const hasMore = cities.length > 0 && cities.length < totalRecords;

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    const nextPage = listPage + 1;
    setLoadingMore(true);
    try {
      const { records } = await fetchCitiesClient(languageId, {
        page: nextPage,
        limit: PAGE_SIZE,
        searchKey: debouncedSearchKey || undefined,
      });
      setCities((prev) => {
        const seen = new Set(prev.map((c) => c.CityId));
        const merged = [...prev];
        for (const r of records) {
          if (!seen.has(r.CityId)) {
            seen.add(r.CityId);
            merged.push(r);
          }
        }
        return merged;
      });
      setListPage(nextPage);
    } catch {
      /* ignore */
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, languageId, listPage, loadingMore, debouncedSearchKey]);

  const selectCity = (c: GogoCityRecord) => {
    const nameEn = (c.CityEn ?? c.City ?? "").trim();
    const nameAr = (c.CityAr ?? nameEn).trim();
    const display = locale === "ar" ? nameAr : nameEn;
    logCityNavClick(`SELECT CITY > ${display.toUpperCase()}`);
    setSavedCityName(display);

    const lat = c.Latitude?.trim();
    const lng = c.Longitude?.trim();
    const apiCoords =
      lat && lng && isValidNearestCoordPair(lat, lng)
        ? { latitude: lat, longitude: lng }
        : null;

    writeSelectedCityCookies(
      c.CityId,
      locale === "ar" ? nameAr : nameEn,
      nameEn,
      apiCoords
    );
    setOpen(false);

    if (apiCoords) {
      writePersistedNearestCoords(apiCoords.latitude, apiCoords.longitude);
      return;
    }

    void (async () => {
      try {
        const data = await fetchRabetCityCoords(nameEn);
        if (
          data.ok &&
          typeof data.latitude === "string" &&
          typeof data.longitude === "string" &&
          isValidNearestCoordPair(data.latitude, data.longitude)
        ) {
          writeSelectedCityCookies(
            c.CityId,
            locale === "ar" ? nameAr : nameEn,
            nameEn,
            { latitude: data.latitude, longitude: data.longitude }
          );
          writePersistedNearestCoords(data.latitude, data.longitude);
        }
      } catch {
        /* Geocode is optional; nav city cookies still apply for display. */
      }
    })();
  };

  const label = cityLabelResolved
    ? (savedCityName ?? t("location.riyadh"))
    : "";

  const citySearchField = (
    <div className="shrink-0 border-b border-[#E5E5E5] bg-white px-2 pb-2 pt-1">
      <div className="relative">
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t("nav.searchCities")}
          className={`w-full rounded-lg border border-[#E5E5E5] bg-[#FAFAFA] py-2 text-sm text-[#130F0E] placeholder:text-[#9CA3AF] focus:border-[#DD0428]/30 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#DD0428]/20 ${isRtl ? "pe-9 ps-3" : "ps-9 pe-3"}`}
          aria-label={t("nav.searchCities")}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        />
        <span
          className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-[#9CA3AF] ${isRtl ? "end-2.5" : "start-2.5"}`}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
        </span>
      </div>
    </div>
  );

  const cityListBody = (
    <div role="listbox" aria-label={t("nav.cityList")}>
      {loading ? (
        <p className="px-4 py-6 text-center text-sm text-[#6B7280]">
          {t("nav.loadingCities")}
        </p>
      ) : loadError ? (
        <p className="px-4 py-6 text-center text-sm text-red-600">
          {t("nav.citiesError")}
        </p>
      ) : cities.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-[#6B7280]">
          {t("nav.noCities")}
        </p>
      ) : (
        <>
          {cities.map((c) => (
            <button
              key={c.CityId}
              type="button"
              role="option"
              onClick={() => selectCity(c)}
              className="flex w-full items-center gap-2 rounded-lg px-4 py-3 text-start text-sm text-[#130F0E] hover:bg-[#F4F4F4] transition-colors md:py-2.5"
            >
              <span className="min-w-0 flex-1 truncate">{cityLabel(c)}</span>
            </button>
          ))}
          {hasMore ? (
            <div className="border-t border-[#E5E5E5] p-2">
              <button
                type="button"
                disabled={loadingMore}
                onClick={() => {
                  logCityNavClick("LOAD MORE CITIES");
                  void loadMore();
                }}
                className="w-full rounded-lg py-2.5 text-center text-sm font-semibold text-[#BD2226] hover:bg-[#FFF5F5] disabled:opacity-50"
              >
                {loadingMore
                  ? t("nav.loadingCities")
                  : t("nav.loadMoreCities")}
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );

  return (
    <>
      <div ref={wrapRef} className={`relative shrink-0 ${className}`}>
        <button
          type="button"
          onClick={() => {
            setOpen((prev) => {
              const next = !prev;
              logCityNavClick(next ? "OPEN CITY POPUP" : "CLOSE CITY POPUP");
              return next;
            });
          }}
          className="flex items-center gap-2 text-[#130F0E] text-sm cursor-pointer hover:text-[#BD2226] transition-colors text-start"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label={t("nav.chooseCity")}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          <span className="font-medium max-w-[10rem] text-[15px] text-[#3A3A3C] truncate">{label}</span>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
            aria-hidden
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        {open && !isNarrowViewport ? (
          <div
            className={`absolute top-full z-[60] mt-2 flex w-[min(calc(100vw-2rem),18rem)] max-h-80 flex-col overflow-hidden rounded-xl border border-[#E5E5E5] bg-white shadow-lg ${
              isRtl ? "end-0" : "start-0"
            }`}
          >
            {citySearchField}
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1 scrollbar-custom">
              {cityListBody}
            </div>
          </div>
        ) : null}
      </div>

      {open && isNarrowViewport ? (
        <MobileBottomSheet
          open
          title={t("nav.chooseCity")}
          closeLabel={t("nav.closeMenu")}
          onClose={() => {
            logCityNavClick("CLOSE CITY POPUP");
            setOpen(false);
          }}
        >
          {citySearchField}
          {cityListBody}
        </MobileBottomSheet>
      ) : null}
    </>
  );
}
